import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { EMPTY, fromEvent, merge, Observable } from 'rxjs';
import { mapTo, filter } from 'rxjs/operators';
import { ControlContainer, FormGroupDirective } from '@angular/forms';
import { InteractiveCell } from '../../types';
import { FcpToastService, ToastRef } from 'fc-ui-components';
import { getItemFromSessionStorage, setItemInSessionStorage } from 'helpers';
import { LanguageService } from 'services';
import { KeyboardNavigateDirection } from '../matrix.component';

const toastTabNavigationSuggestionShownSessionStorageKey = 'tabNavigationSuggestionOrderMatrix';

@Component({
    selector: 'fc-order-input-cell',
    templateUrl: './input.component.html',
    styleUrls: ['./input.component.less'],
    viewProviders: [
        {
            provide: ControlContainer,
            useExisting: FormGroupDirective
        }
    ],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class OrderInputComponent implements OnInit, OnDestroy, OnChanges {
    @Input() cell: InteractiveCell;
    @Input() formControlName: string;
    @Input() public tooltipTemplateRef: TemplateRef<any>;
    @Input() public rowIndex: number;
    @Input() public columnIndex: number;
    @Input() public wide = false;

    @ViewChild('cellInput', { static: true, read: ElementRef }) private cellInput: ElementRef;
    @ViewChild('cellIcon', { static: true, read: ElementRef }) private cellIcon: ElementRef;

    @Output('blur') public blurChanges: EventEmitter<FocusEvent> = new EventEmitter();
    @Output('keyboardNavigate') public keyboardNavigate: EventEmitter<KeyboardNavigateDirection> = new EventEmitter();
    public isTooltipVisible: Observable<boolean>;
    public isIconTooltipVisible: Observable<boolean>;
    private toastRef: ToastRef;

    public tooltipPosition = {
        originX: 'center',
        originY: 'top'
    };

    constructor(
        private fcpToastService: FcpToastService,
        private languageService: LanguageService
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.cell?.currentValue?.iconTooltip) {
            this.isIconTooltipVisible = this.setupIconTooltipVisibility(this.cellIcon.nativeElement);
        }
    }

    ngOnInit() {
        if (this.cell.inputTooltip) {
            this.isTooltipVisible = this.setupInputTooltipVisibility(this.cellInput.nativeElement);
        }
    }

    async onClick() {
        if (!getItemFromSessionStorage(toastTabNavigationSuggestionShownSessionStorageKey)) {
            if (this.toastRef && this.toastRef.isVisible()) {
                this.toastRef.close();
            }

            const translatedLabelMap = await this.languageService.translate([
                'ORDER_MATRIX.TAB_NAVIGATION_SUGGESTION_TOAST'
            ]);
            this.toastRef = this.fcpToastService.show({
                text: translatedLabelMap['ORDER_MATRIX.TAB_NAVIGATION_SUGGESTION_TOAST'],
                icon: ['far', 'arrows'],
                iconBorder: true,
                timeout: 6000
            });
            const toastDiv = this.toastRef.isVisible().querySelector('div.toast');
            toastDiv.classList.add('desktop-only');
            setItemInSessionStorage(toastTabNavigationSuggestionShownSessionStorageKey, true);
        }
    }

    ngOnDestroy() {
        this.removeKeyboardEventListener();
    }

    public onInputBlur(event: FocusEvent) {
        this.blurChanges.emit(event);
    }

    public onInputFocusIn(event: FocusEvent) {
        this.inputElement.addEventListener('keydown', this.handleKeyboardEvent.bind(this));
    }

    public onInputFocusOut(event: FocusEvent) {
        this.removeKeyboardEventListener();
    }

    handleKeyboardEvent(event: KeyboardEvent) {
        if (event.repeat) {
            return;
        }

        if (Object.values(NavigationKeys).includes(event.key as NavigationKeys)) {
            // Stop propagation to prevent document-level listeners from handling these keys
            event.stopPropagation();

            switch (event.key) {
                case NavigationKeys.ArrowUp:
                    return this.keyboardNavigate.emit(KeyboardNavigateDirection.PreviousRow);
                case NavigationKeys.ArrowDown:
                    return this.keyboardNavigate.emit(KeyboardNavigateDirection.NextRow);
                case NavigationKeys.ArrowLeft:
                    return this.keyboardNavigate.emit(KeyboardNavigateDirection.PreviousColumn);
                default:
                    return this.keyboardNavigate.emit(KeyboardNavigateDirection.NextColumn);
            }
        }
    }

    static getInputId(rowIndex: number, columnIndex: number, cellId: string | number) {
        return `matrix-input-${cellId}-${rowIndex}-${columnIndex}`;
    }

    get inputId() {
        return OrderInputComponent.getInputId(this.rowIndex, this.columnIndex, this.cell.id);
    }

    get inputElement() {
        return document.getElementById(this.inputId);
    }

    private createHoverEvents(element) {
        const mouseEnterEvent = fromEvent(element, 'mouseenter').pipe(mapTo(true));
        const mouseLeaveEvent = fromEvent(element, 'mouseleave').pipe(mapTo(false));

        return merge(mouseEnterEvent, mouseLeaveEvent);
    }

    private setupIconTooltipVisibility(element) {
        const hoverEvents = this.createHoverEvents(element);
        const focusInEvent = fromEvent(this.cellInput.nativeElement, 'focusin').pipe(mapTo(true));
        const focusOutEvent = fromEvent(this.cellInput.nativeElement, 'focusout').pipe(mapTo(false));

        return merge(hoverEvents, focusInEvent, focusOutEvent);
    }

    private setupInputTooltipVisibility(element) {
        const hoverEvents = this.createHoverEvents(element);
        const hideOnIconTooltipVisible = (this.isIconTooltipVisible ?? EMPTY).pipe(
            filter((isVisible) => isVisible),
            mapTo(false)
        );

        return merge(hideOnIconTooltipVisible, hoverEvents);
    }

    private removeKeyboardEventListener() {
        if (this.inputElement) {
            this.inputElement.removeAllListeners('keydown');
        }
    }
}

enum NavigationKeys {
    ArrowUp = 'ArrowUp',
    ArrowDown = 'ArrowDown',
    ArrowLeft = 'ArrowLeft',
    ArrowRight = 'ArrowRight'
}
